import apiClient from '../client';

export interface CustomerDatafeedRecipient {
  Id: number;
  DatafeedId: number;
  Email: string;
  IsCc: boolean;
}

export interface CustomerDatafeedSchedule {
  Id: number;
  DatafeedId: number;
  ScheduleTime: string;      // HH:mm
  DayOfWeek: number | null;  // 0=Sun..6=Sat
  Frequency: string;         // 'daily' | 'weekly' | 'biweekly' | 'monthly'
  DayOfMonth: number | null; // 1–31, for monthly
  WeekParity: number | null; // 0 or 1, for biweekly
}

export interface CustomerDatafeed {
  Id: number;
  CustomerName: string;
  S3Path: string | null;
  EmailTemplate: string;
  Recipients: CustomerDatafeedRecipient[];
  Schedules: CustomerDatafeedSchedule[];
}

export const customerDatafeedService = {
  async getAll(): Promise<CustomerDatafeed[]> {
    const response = await apiClient.get<CustomerDatafeed[]>('/customer-datafeeds');
    return response.data;
  },

  async create(customerName: string, emailTemplate: string, s3Path?: string | null): Promise<{ id: number }> {
    const response = await apiClient.post<{ id: number }>('/customer-datafeeds', { customerName, emailTemplate, s3Path: s3Path ?? null });
    return response.data;
  },

  async update(id: number, customerName: string, s3Path?: string | null, emailTemplate?: string): Promise<void> {
    await apiClient.put(`/customer-datafeeds/${id}`, { customerName, s3Path: s3Path ?? null, emailTemplate: emailTemplate ?? 'datafeed' });
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/customer-datafeeds/${id}`);
  },

  async addRecipient(datafeedId: number, email: string, isCc: boolean = false): Promise<{ id: number }> {
    const response = await apiClient.post<{ id: number }>(
      `/customer-datafeeds/${datafeedId}/recipients`,
      { email, isCc }
    );
    return response.data;
  },

  async removeRecipient(datafeedId: number, recipientId: number): Promise<void> {
    await apiClient.delete(`/customer-datafeeds/${datafeedId}/recipients/${recipientId}`);
  },

  async testS3Access(datafeedId: number): Promise<{
    bucket: string;
    prefix: string;
    fileCount: number;
    files: { key: string; size: number; lastModified: string }[];
    wouldSend: string | null;
  }> {
    const response = await apiClient.get(`/customer-datafeeds/${datafeedId}/test-s3`);
    return response.data;
  },

  async runNow(datafeedId: number): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>(`/customer-datafeeds/${datafeedId}/run`);
    return response.data;
  },

  async addSchedule(
    datafeedId: number,
    scheduleTime: string,
    dayOfWeek: number | null,
    frequency: string,
    dayOfMonth: number | null,
  ): Promise<{ id: number }> {
    const response = await apiClient.post<{ id: number }>(
      `/customer-datafeeds/${datafeedId}/schedules`,
      { scheduleTime, dayOfWeek, frequency, dayOfMonth }
    );
    return response.data;
  },

  async removeSchedule(datafeedId: number, scheduleId: number): Promise<void> {
    await apiClient.delete(`/customer-datafeeds/${datafeedId}/schedules/${scheduleId}`);
  },
};
